/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal, ViewEncapsulation, computed } from '@angular/core';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AppConfigService, AuthenticationService } from '@alfresco/adf-core';
import { AlfrescoApiService, SearchService } from '@alfresco/adf-content-services';
import { ResultSetPaging, WebscriptApi } from '@alfresco/js-api';
import { TranslatePipe } from '@ngx-translate/core';
import { catchError, from, of } from 'rxjs';

/**
 * Storage used, at the foot of the sidebar.
 *
 * Both halves of "x of y" come from the server where the server has them:
 *
 * - **A quota is set on the person.** The allowance and the usage both come
 *   from the classic `api/people/{id}` webscript (the v1 `Person` this
 *   application uses everywhere else carries neither), because `sizeCurrent`
 *   is the number Alfresco actually measures the quota against.
 * - **No quota** — Alfresco's default, reported as -1, and the case on a stock
 *   server. The usage is then real but computed here: the size of the
 *   documents the person created, summed from a search. The allowance falls
 *   back to `storage.quotaBytes` in app.config.json, which is this
 *   application's own figure and is NOT enforced by the repository — set a
 *   quota on the account and the server's number takes over. The tooltip says
 *   which of the two is on screen.
 *
 * The sum is taken over one page of results; past that the figure is reported
 * as a floor rather than silently undercounting.
 */
@Component({
  selector: 'app-storage-usage',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule, MatTooltipModule, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  template: `
    <section class="aca-storage" *ngIf="ready()">
      <header class="aca-storage__head">
        <span class="aca-storage__title">{{ 'DMS.SHELL.STORAGE' | translate }}</span>
        <mat-icon class="aca-storage__info" [matTooltip]="(quotaFromServer() ? 'DMS.SHELL.STORAGE_HINT' : 'DMS.SHELL.STORAGE_HINT_APP') | translate">
          info_outline
        </mat-icon>
      </header>

      <div
        class="aca-storage__bar"
        role="progressbar"
        [attr.aria-valuenow]="percent()"
        aria-valuemin="0"
        aria-valuemax="100"
        [attr.aria-label]="'DMS.SHELL.STORAGE' | translate"
      >
        <span
          class="aca-storage__fill"
          [class.aca-storage__fill--any]="used() > 0"
          [class.aca-storage__fill--full]="percent() >= 90"
          [style.width.%]="percent()"
        ></span>
      </div>

      <p class="aca-storage__text">
        {{ (approximate() ? 'DMS.SHELL.STORAGE_USED_AT_LEAST' : 'DMS.SHELL.STORAGE_USED') | translate: { used: usedLabel(), total: quotaLabel() } }}
      </p>
      <a class="aca-storage__link" routerLink="/profile">{{ 'DMS.SHELL.STORAGE_DETAILS' | translate }}</a>
    </section>
  `
})
export class StorageUsageComponent implements OnInit {
  private readonly api = inject(AlfrescoApiService);
  private readonly auth = inject(AuthenticationService);
  private readonly search = inject(SearchService);
  private readonly config = inject(AppConfigService);

  /** How many documents one sum covers; past this the figure is a floor. */
  private static readonly SUM_PAGE = 1000;
  /** Used when neither the account nor the configuration names an allowance. */
  private static readonly FALLBACK_QUOTA = 10 * 1024 * 1024 * 1024;

  /** The allowance the bar is drawn against, in bytes. */
  readonly quota = signal(0);
  readonly used = signal(0);
  /** True once there is a figure to show — the card is not drawn before that. */
  readonly ready = signal(false);
  /** The person has more documents than one page, so `used` is a lower bound. */
  readonly approximate = signal(false);
  /** Whether the allowance is the repository's or this application's own. */
  readonly quotaFromServer = signal(false);

  readonly percent = computed(() => {
    const quota = this.quota();
    return quota > 0 ? Math.min(100, Math.round((this.used() / quota) * 100)) : 0;
  });

  readonly usedLabel = computed(() => StorageUsageComponent.format(this.used()));
  readonly quotaLabel = computed(() => StorageUsageComponent.format(this.quota()));

  ngOnInit(): void {
    const user = this.auth.getUsername();
    if (!user) {
      return;
    }

    const ws = new WebscriptApi(this.api.getInstance() as any);
    from(ws.executeWebScript('GET', `api/people/${encodeURIComponent(user)}`) as Promise<any>)
      .pipe(catchError(() => of(null)))
      .subscribe((person: any) => {
        const quota = Number(person?.quota ?? -1);
        this.quota.set(quota);

        if (quota > 0) {
          this.quotaFromServer.set(true);
          this.used.set(Math.max(0, Number(person?.sizeCurrent ?? 0)));
          this.ready.set(true);
          return;
        }

        this.quota.set(this.configuredQuota());
        this.sumOwnDocuments(user);
      });
  }

  /**
   * What the person's own documents occupy.
   *
   * Thumbnails and renditions are excluded: they are the repository's copies,
   * not anything anyone filed, and counting them would make the figure larger
   * than the documents the person can actually see.
   */
  private sumOwnDocuments(user: string): void {
    const query = `TYPE:"cm:content" AND cm:creator:"${user}" AND -TYPE:"cm:thumbnail" AND -TYPE:"rn:rendition"`;

    this.search
      .searchByQueryBody({
        query: { query, language: 'afts' },
        paging: { maxItems: StorageUsageComponent.SUM_PAGE, skipCount: 0 },
        fields: ['id', 'content'],
        include: []
      } as any)
      .pipe(catchError(() => of(null)))
      .subscribe((results: ResultSetPaging | null) => {
        const entries = results?.list?.entries ?? [];
        const total = entries.reduce((sum, entry) => sum + Number((entry.entry as any)?.content?.sizeInBytes ?? 0), 0);
        this.used.set(total);
        this.approximate.set(!!results?.list?.pagination?.hasMoreItems);
        this.ready.set(true);
      });
  }

  private configuredQuota(): number {
    const configured = Number(this.config.get<number>('storage.quotaBytes', StorageUsageComponent.FALLBACK_QUOTA));
    return configured > 0 ? configured : StorageUsageComponent.FALLBACK_QUOTA;
  }

  /** Bytes as the shortest unit that keeps the number readable (1 KB = 1024 B). */
  private static format(bytes: number): string {
    if (!bytes || bytes < 0) {
      return '0 B';
    }
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    const step = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
    const value = bytes / Math.pow(1024, step);
    return `${value >= 10 || step === 0 ? Math.round(value) : value.toFixed(1)} ${units[step]}`;
  }
}
